import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";
import { cityPriceFaq } from "./cityPricing";

const theouleSurMer: ChauffeurPage = {
  slug: "theoule-sur-mer",
  title: "Chauffeur privé à Théoule-sur-Mer – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Théoule-sur-Mer : transferts aéroport de Nice, massif de l’Estérel, criques et Corniche d’Or, 7j/7.",
  keywords: [
    "chauffeur privé théoule-sur-mer",
    "vtc théoule",
    "chauffeur theoule sur mer",
    "transfert théoule aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Théoule-sur-Mer",
    subtitle:
      "Transferts aéroport de Nice, roches rouges de l’Estérel, criques et Corniche d’Or — disponible 24h/24, 7j/7.",
    keywords: [
      "VTC Théoule",
      "chauffeur Estérel",
      "transfert aéroport Nice",
      "Corniche d’Or",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Théoule-sur-Mer",
    badges: ["Disponible 24h/24", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Théoule-sur-Mer",
    items: [
      {
        title: "Aéroport de Nice en direct",
        description:
          "Aéroport de Nice à environ 45 min par l’A8, suivi de vol et accueil personnalisé.",
      },
      {
        title: "Routes de l’Estérel maîtrisées",
        description:
          "Corniche d’Or, virages et criques : le chauffeur gère la route sinueuse et le stationnement rare.",
      },
      {
        title: "Chauffeurs bilingues",
        description:
          "FR/EN, ponctuels et discrets, à l’aise sur les corniches et l’arrière-pays.",
      },
      {
        title: "Ancrage local",
        description: "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Théoule-sur-Mer",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Prise en charge avec suivi de vol depuis et vers l’aéroport de Nice Côte d’Azur et Cannes-Mandelieu.",
      },
      {
        title: "Liaisons avec Cannes",
        text: "De l’autre côté de la baie, la Croisette et le Palais des Festivals à quelques minutes.",
      },
      {
        title: "Estérel & Corniche d’Or",
        text: "Balades panoramiques entre roches rouges et mer, du Trayas à Saint-Raphaël, au rythme de la lumière.",
      },
      {
        title: "Criques, ports & Palais Bulles",
        text: "Port de la Figueirette, port de Théoule, La Galère et les criques, avec dépose au plus près.",
      },
      {
        title: "Mise à disposition & longue distance",
        text: "À l’heure, à la journée ou toute distance : Cannes, Saint-Tropez, Monaco et Italie.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Massif de l’Estérel",
        description: "Roches rouges, Cap Roux et sentiers face à la mer.",
      },
      {
        name: "Corniche d’Or",
        description:
          "Route panoramique du littoral vers Le Trayas et Saint-Raphaël.",
      },
      {
        name: "Port de la Figueirette",
        description: "Port de plaisance au pied de Miramar.",
      },
      {
        name: "Château de la Théoule",
        description: "Ancienne savonnerie devenue château, en bord de mer.",
      },
      {
        name: "Pointe de l’Aiguille",
        description: "Parc départemental, criques et sentier du littoral.",
      },
      {
        name: "Aéroport de Nice",
        description: "À environ 45 minutes par l’autoroute A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur privé pour tout Théoule-sur-Mer",
    subheading:
      "Entre roches rouges et mer, un service premium sur les routes de l’Estérel.",
    paragraphs: [
      "KS Rent Services assure vos déplacements avec chauffeur privé à Théoule-sur-Mer et sur toute la Côte d’Azur : transferts depuis l’aéroport de Nice (environ 45 minutes), balades dans l’Estérel et trajets longue distance.",
      "Adossée au massif de l’Estérel, Théoule aligne criques, ports et roches rouges le long de la Corniche d’Or. Nos chauffeurs bilingues maîtrisent la route sinueuse et vous déposent au plus près, sans tourner pour se garer.",
      "De l’autre côté de la baie de Cannes, Théoule reste à quelques minutes de la Croisette : transfert le matin, randonnée dans l’Estérel et dîner à Cannes le soir, votre chauffeur enchaîne les étapes.",
    ],
    bullets: [
      "Suivi de vol & accueil personnalisé",
      "Corniche d’Or panoramique",
      "Liaisons Cannes & Estérel",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Théoule-sur-Mer",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Nous confirmons le véhicule adapté et le forfait.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Ajustements en direct selon le trafic et vos imprévus.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Théoule-sur-Mer"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Théoule ?",
      a: "Comptez environ 45 minutes par l’autoroute A8 dans des conditions normales. Nous assurons les transferts 24h/24 avec suivi de vol et accueil personnalisé.",
    },
    {
      q: "Théoule est-elle proche de Cannes ?",
      a: "Oui, de l’autre côté de la baie : nous rejoignons la Croisette et le Palais des Festivals en quelques minutes, et l’aéroport de Nice en environ 45 minutes.",
    },
    {
      q: "Proposez-vous des balades dans l’Estérel et sur la Corniche d’Or ?",
      a: "Oui, un itinéraire panoramique entre roches rouges et criques, du Trayas à Saint-Raphaël, avec arrêts photo possibles.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Théoule-sur-Mer ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Pouvez-vous nous déposer pour une randonnée dans l’Estérel ?",
      a: "Oui, dépose aux départs de sentiers (Pointe de l’Aiguille, Cap Roux) et reprise à la demande, sans souci de stationnement.",
    },
  ],
  relatedLinks: [
    { label: "Réserver un chauffeur à Cannes", href: "/chauffeur/cannes" },
    { label: "Transfert aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "Chauffeur privé Côte d’Azur", href: "/chauffeur/cote-d-azur" },
    {
      label: "Location de véhicule à Cannes",
      href: "/location/location-voiture-cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Théoule-sur-Mer",
    text: "Réservez votre transfert ou votre mise à disposition — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Théoule-sur-Mer — VTC 24h/24",
    description:
      "Chauffeur privé et VTC à Théoule-sur-Mer : transfert aéroport de Nice (~45 min), massif de l’Estérel, Corniche d’Or et criques. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/theoule-sur-mer",
  },
};

export default theouleSurMer;
